import { useCallback, useEffect, useState } from 'react';

/**
 * The colour theme.
 *
 * Three states, not two, and that is the point: **`system` is a real choice**, not the absence of
 * one. A reader whose machine switches to light at sunset should get light, until they say
 * otherwise — and then the site must remember that they said so. Storing only 'light' | 'dark'
 * means the first visit is a guess and there is no way to hand the decision back.
 *
 * The theme is written to `document.documentElement.dataset.theme`, which is what the stylesheet
 * keys on. That happens in `index.html` before first paint as well (see the boot script there), so
 * a returning reader never sees the wrong theme flash before React mounts.
 */

export type Theme = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'inscribed-audio-theme';

function stored(): Theme {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    // Private-mode Safari throws on localStorage access. Falling through to `system` is correct:
    // the preference simply does not persist, and the site still works.
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

/** The theme actually rendered, after resolving `system`. */
export function resolved(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return theme;
}

function apply(theme: Theme) {
  const effective = resolved(theme);
  document.documentElement.dataset.theme = effective;
  // The browser paints form controls, scrollbars and the URL bar from this.
  document.documentElement.style.colorScheme = effective;
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => stored());

  // Apply on mount and whenever the choice changes.
  useEffect(() => {
    apply(theme);
  }, [theme]);

  // While the choice is `system`, follow the machine as it changes — and stop following the moment
  // an explicit choice is made, which is what the `theme` dependency does.
  useEffect(() => {
    if (theme !== 'system' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => apply('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Non-fatal, as above.
    }
  }, []);

  // Cycle: system -> light -> dark -> system. A cycle rather than two buttons because the control
  // has to fit the header on a phone, and the current state is shown by its icon.
  const cycle = useCallback(() => {
    setTheme(theme === 'system' ? 'light' : theme === 'light' ? 'dark' : 'system');
  }, [theme, setTheme]);

  return { theme, effective: resolved(theme), setTheme, cycle };
}

/** The control itself. One button, cycling, with the state in its label and title. */
export function ThemeToggle() {
  const { theme, cycle } = useTheme();
  const label =
    theme === 'system' ? 'Theme: following your system' : `Theme: ${theme}`;
  const glyph = theme === 'system' ? '◐' : theme === 'light' ? '☀' : '☾';

  return (
    <button className="theme-toggle" onClick={cycle} aria-label={label} title={label}>
      <span aria-hidden="true">{glyph}</span>
      <span className="theme-toggle__text">{theme}</span>
    </button>
  );
}
